import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowRight } from 'lucide-react';
import { servicesSeedData } from '../database/seedData/services';
import ServiceCatalogCard from '../components/ServiceCatalogCard';
import { AdvisoryLanesDiagram, ExecutiveFlowRail } from '../components/executive';

// Impact metrics removed - no fabricated data

const engagementRhythm = [
  { title: 'Orient', description: 'Shared view of mandate, constraints, and the decisions leadership faces.' },
  { title: 'Weigh', description: 'Independent perspective on investment, ownership, and risk before commitments harden.' },
  { title: 'Pressure-Test', description: 'Explicit conditions for proceed, pause, or stop—not open-ended experimentation.' },
  { title: 'Advise', description: 'Ongoing counsel as priorities evolve. No fixed delivery playbook.' },
];

const Services = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [servicesData, setServicesData] = useState(servicesSeedData);

  useEffect(() => {
    setServicesData(servicesSeedData);
  }, []);

  // If no services are available, show a loading state
  if (!servicesData || servicesData.length === 0) {
    return (
      <div className={`container mx-auto px-4 py-12 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <h1 className={`text-4xl font-bold text-center mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-800'}`}>
          Our Services
        </h1>
        <p className={`text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'} text-center mb-12 max-w-3xl mx-auto`}>
          Loading Services...
        </p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-800'}`}>
      {/* Hero */}
      <section className={`relative overflow-hidden ${theme === 'dark' ? 'bg-slate-950' : 'bg-gradient-to-b from-white to-slate-50'} py-24 lg:py-32`}>
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid xl:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className={`inline-block px-4 py-1.5 rounded-full ${theme === 'dark' ? 'bg-slate-800 border border-slate-700' : 'bg-blue-50 border border-blue-100'}`}>
                <span className={`text-xs uppercase tracking-[0.3em] ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>Advisory Services</span>
              </div>
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
                Independent counsel across AI, data, and agentic systems.
              </h1>
              <p className={`text-xl lg:text-2xl leading-relaxed ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>
                Three advisory lanes—one standard of executive judgment.
              </p>
            </div>
            <AdvisoryLanesDiagram theme={theme} />
          </div>
        </div>
      </section>

      {/* Impact Metrics section removed - no fabricated data */}

      {/* Services Catalog */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {servicesData.map((service) => (
              <ServiceCatalogCard
                key={service.slug}
                service={service}
                theme={theme}
                onClick={() => navigate(`/services/${service.slug}`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Engagement rhythm */}
      <section className={`${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'} py-20`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className={`text-xs uppercase tracking-[0.4em] mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>Engagement Rhythm</p>
            <h2 className={`text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-900'}`}>How Advisory Engagements Unfold</h2>
          </div>
          <div className="max-w-6xl mx-auto">
            <ExecutiveFlowRail phases={engagementRhythm} theme={theme} variant="neutral" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`${theme === 'dark' ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900'} py-20`}>
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <p className="uppercase tracking-[0.4em] text-xs mb-6 text-white/60">Partner Engagement</p>
          <h2 className="text-3xl lg:text-4xl font-semibold mb-6 text-white">Need Direction For AI Investment?</h2>
          <p className="text-lg text-white/80 mb-8">We’ll help you clarify priorities and whether independent counsel fits your situation.</p>
          <div className="flex justify-center">
            <Link to="/contact" className="px-10 py-4 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors">Schedule Working Session</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
